import { useSiteReducedMotion, useForwardProgress } from "./motion-preferences";
import { useState, useRef } from "react";
import { motion, useScroll } from "framer-motion";
import { ArrowUpRight, Scan, PenTool, Code2, Rocket } from "lucide-react";
import { Reveal, ScrollHeading, Eyebrow } from "./ui";
const steps = [
  {
    title: "Discovery",
    description:
      "We discuss your goals, users, current systems, and constraints to define the problem and agree on what success looks like.",
    icon: Scan,
    caption: "Start with the right problem.",
  },
  {
    title: "Planning",
    description:
      "We recommend an approach, outline the scope, and agree on priorities, milestones, and an initial estimate before development begins.",
    icon: PenTool,
    caption: "Make the next steps clear.",
  },
  {
    title: "Development",
    description:
      "We build in manageable stages, using AI where it helps with prototyping and repetitive tasks. We review and test the work, then refine it with your feedback.",
    icon: Code2,
    caption: "Build, test, and refine.",
  },
  {
    title: "Launch & Support",
    description:
      "We help prepare the release, document the handover, and agree on any maintenance, support, or further development you need.",
    icon: Rocket,
    caption: "Plan for what comes next.",
  },
];
export function Process() {
  const stepsRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: stepsRef,
    offset: ["start 80%", "end 65%"],
  });
  const scaleY = useForwardProgress(scrollYProgress);
  const [active, setActive] = useState(0);
  const reduced = useSiteReducedMotion();
  const Icon = steps[active].icon;
  return (
    <section className="process section-pad" id="process">
      <div className="wrap">
        <Reveal className="process-heading">
          <Eyebrow number="05">Our process</Eyebrow>
          <ScrollHeading
            lines={[
              { text: "From concept", muted: false },
              { before: "to ", text: "reality.", muted: true },
            ]}
          />
          <span className="process-aside">
            CLEAR SCOPE.
            <br />
            SHARED DECISIONS.
          </span>
        </Reveal>
        <div className="process-layout">
          <Reveal className="process-illustration">
            <div className="process-illustration-top">
              <span>THE QUANTUM PROCESS</span>
              <span>0{active + 1} / 04</span>
            </div>
            <motion.div
              key={active}
              className="process-detail"
              aria-live="polite"
              initial={reduced ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              <span className="process-large-number">0{active + 1}</span>
              <Icon size={30} strokeWidth={1.2} />
              <h3>{steps[active].title}</h3>
              <p>{steps[active].description}</p>
            </motion.div>
            <div className="process-illustration-bottom" aria-live="polite">
              <span>{steps[active].caption}</span>
              <ArrowUpRight size={24} />
            </div>
          </Reveal>
          <div className="process-steps" ref={stepsRef}>
            <div className="process-scroll-track" aria-hidden="true">
              <motion.span style={{ scaleY: reduced ? 1 : scaleY }} />
            </div>
            {steps.map((step, i) => (
              <Reveal key={step.title} delay={i * 0.06}>
                <button
                  className={`process-step ${active === i ? "active" : ""}`}
                  onClick={() => setActive(i)}
                  aria-pressed={active === i}
                >
                  <span className="step-number">0{i + 1}</span>
                  <span className="step-content">
                    <strong>{step.title}</strong>
                    <span>{step.description}</span>
                  </span>
                  <ArrowUpRight size={23} />
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
